/**
 * Storefront support form → bridge DB-first intake (gh-6239, gh-6761).
 * Intercepts support ticket and contact support forms when bridge intake is enabled.
 */
(function () {
  "use strict";

  var SECTION_HANDLES = [
    "pma__support_ticket_form",
    "pma__support__contact_split",
    "pma__support__form",
  ];
  var SUPPORT_FORM_SELECTOR =
    'form[data-support-form], form[data_support_form], form.pma-support-ticket-form, form.pma-support-contact-form';
  var DEFAULT_LOADING_MESSAGE = "Sending support message...";
  var TURNSTILE_CONFIG_ENDPOINT = "/api/newsletter-config";
  var TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
  var SUBMIT_COOLDOWN_MS = 45000;
  var STORAGE_PREFIX = "pma-support-intake-last-submit:";

  var turnstilePromise = null;
  var turnstileConfigPromise = null;

  function findSupportForm(section) {
    if (!section) return null;
    return section.querySelector(SUPPORT_FORM_SELECTOR);
  }

  function findSubmitButton(form) {
    if (!form) return null;
    return form.querySelector("[data-pma-form-submit], button[type='submit']");
  }

  function readConfig(section) {
    if (!section) return null;
    var url = (section.getAttribute("data-support-intake-url") || "").trim();
    if (!url) return null;
    return {
      url: url,
      enabled: section.getAttribute("data-support-intake-enabled") === "true",
      surface: section.getAttribute("data-pma-support-surface") || "support_form",
      msgSending:
        section.getAttribute("data-intake-msg-sending") || DEFAULT_LOADING_MESSAGE,
      msgProjected: section.getAttribute("data-intake-msg-projected") || "",
      msgQueued: section.getAttribute("data-intake-msg-queued") || "",
      msgPending: section.getAttribute("data-intake-msg-pending") || "",
      msgTurnstileRequired:
        section.getAttribute("data-intake-msg-turnstile-required") ||
        "Complete the Cloudflare verification before sending your message.",
      msgCooldown:
        section.getAttribute("data-intake-msg-cooldown") ||
        "Please wait a few seconds before submitting again. This brief pause helps protect support from automated spam.",
    };
  }

  function fetchTurnstileConfig() {
    if (turnstileConfigPromise) return turnstileConfigPromise;
    turnstileConfigPromise = fetch(TURNSTILE_CONFIG_ENDPOINT, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then(function (response) {
        if (!response.ok) throw new Error("turnstile_config_unavailable");
        return response.json();
      })
      .catch(function () {
        return { ok: false, turnstileSiteKey: "" };
      });
    return turnstileConfigPromise;
  }

  function loadTurnstile() {
    if (window.turnstile && typeof window.turnstile.render === "function") {
      return Promise.resolve(window.turnstile);
    }
    if (turnstilePromise) return turnstilePromise;
    turnstilePromise = new Promise(function (resolve, reject) {
      var script = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
      if (!script) {
        script = document.createElement("script");
        script.src = TURNSTILE_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", function () {
        if (window.turnstile && typeof window.turnstile.render === "function") {
          resolve(window.turnstile);
        } else {
          reject(new Error("turnstile_missing"));
        }
      });
      script.addEventListener("error", function () {
        reject(new Error("turnstile_failed"));
      });
    });
    return turnstilePromise;
  }

  function ensureTurnstile(form, siteKey) {
    var target = form.querySelector("[data-pma-turnstile]");
    if (!target || !siteKey || target.getAttribute("data-rendered") === "true") {
      return;
    }
    loadTurnstile()
      .then(function (turnstile) {
        var widgetId = turnstile.render(target, {
          sitekey: siteKey,
          theme: "auto",
          size: "flexible",
          callback: function (token) {
            form.setAttribute("data-turnstile-token", token || "");
          },
          "expired-callback": function () {
            form.setAttribute("data-turnstile-token", "");
          },
          "error-callback": function () {
            form.setAttribute("data-turnstile-token", "");
          },
        });
        target.setAttribute("data-rendered", "true");
        target.setAttribute("data-widget-id", widgetId);
      })
      .catch(function () {
        showIntakeError(
          form,
          "Cloudflare verification could not load. Please refresh the page and try again.",
        );
      });
  }

  function resetTurnstile(form) {
    form.setAttribute("data-turnstile-token", "");
    var widget = form.querySelector("[data-pma-turnstile]");
    var widgetId = widget && widget.getAttribute("data-widget-id");
    if (widgetId && window.turnstile && typeof window.turnstile.reset === "function") {
      try {
        window.turnstile.reset(widgetId);
      } catch (_e) {}
    }
  }

  function storageKey(form) {
    return STORAGE_PREFIX + (form.id || "support-intake");
  }

  function cooldownRemainingMs(form) {
    try {
      var last = parseInt(sessionStorage.getItem(storageKey(form)) || "0", 10);
      if (!last) return 0;
      var remaining = SUBMIT_COOLDOWN_MS - (Date.now() - last);
      return remaining > 0 ? remaining : 0;
    } catch (_e) {
      return 0;
    }
  }

  function markSubmitted(form) {
    try {
      sessionStorage.setItem(storageKey(form), String(Date.now()));
    } catch (_e) {}
  }

  function fieldValue(form, name) {
    var el = form.querySelector('[name="' + name + '"]');
    return el && typeof el.value === "string" ? el.value.trim() : "";
  }

  function memberStateFromForm(form) {
    var raw = fieldValue(form, "contact[context_member_state]");
    if (raw === "member") return "member";
    if (raw === "pending") return "pending";
    if (raw === "denied") return "denied";
    return "non_member";
  }

  function mapSupportCategory(raw) {
    var value = String(raw || "").trim();
    var legacy = {
      order_status_tracking: "orders_delivery",
      shipping_issue: "orders_delivery",
      membership_application: "membership_join_approval",
      account_login: "account_access",
      product_question: "product_questions",
      general_question: "other_general",
    };
    if (legacy[value]) return legacy[value];
    return value || "other_general";
  }

  function buildPayload(form, section) {
    var category = mapSupportCategory(fieldValue(form, "contact[category]") || "other_general");
    var summary =
      fieldValue(form, "contact[summary]") ||
      fieldValue(form, "contact[subject]") ||
      category.replace(/_/g, " ");
    var details = fieldValue(form, "contact[body]");
    var email = fieldValue(form, "contact[email]");
    var name = fieldValue(form, "contact[name]");
    var orderRef =
      fieldValue(form, "contact[order_reference]") || fieldValue(form, "contact[order_number]");
    var memberRef =
      fieldValue(form, "contact[membership_reference]") ||
      fieldValue(form, "contact[membership_email]");
    var preferred = fieldValue(form, "contact[preferred_contact]");
    var urgency = fieldValue(form, "contact[urgency]");
    var bodyText = details;
    if (!bodyText && summary) {
      bodyText = summary;
    }
    if (urgency && urgency !== "standard") {
      bodyText = "[Urgency: " + urgency + "]\n\n" + bodyText;
    }

    var payload = {
      category: category,
      summary: summary,
      body: bodyText,
      preferred_contact: preferred || null,
      order_reference: orderRef || null,
      membership_reference: memberRef || null,
      contact: { name: name, email: email },
      context: {
        context_member_state: memberStateFromForm(form),
        context_page_type:
          fieldValue(form, "contact[context_page_type]") ||
          section.getAttribute("data-context-page-type") ||
          "support_hub",
        context_template:
          fieldValue(form, "contact[context_template]") ||
          section.getAttribute("data-context-template") ||
          "page.support-hub",
        context_lock_surface: section.getAttribute("data-context-lock-surface") || null,
      },
      source_url: window.location.href,
      submission_type: fieldValue(form, "contact[submission_type]") || "support_contact",
      anti_automation: {
        rendered_at: Number(form.getAttribute("data-support-intake-rendered-at") || Date.now()),
      },
    };

    var token = form.getAttribute("data-turnstile-token") || "";
    if (token) {
      payload["cf-turnstile-response"] = token;
      payload.anti_automation.turnstile_token = token;
    }

    return payload;
  }

  function deskStatusMessage(config, deskStatus) {
    if (deskStatus === "projected" && config.msgProjected) return config.msgProjected;
    if ((deskStatus === "dry_run" || deskStatus === "pending") && config.msgQueued) {
      return config.msgQueued;
    }
    if (
      (deskStatus === "projection_failed" || deskStatus === "validation_failed") &&
      config.msgPending
    ) {
      return config.msgPending;
    }
    if (deskStatus === "projected") {
      return "Thank you. Your support message was sent and a support ticket was created.";
    }
    if (deskStatus === "projection_failed" || deskStatus === "validation_failed") {
      return "Thank you. Your support request was recorded. Ticket creation is still processing.";
    }
    return "Thank you. Your support message was received and queued for review.";
  }

  function deskTicketLine(deskStatus, deskTicketId) {
    if (deskStatus !== "projected" || !deskTicketId) return "";
    return (
      '<p class="pma-text-sm">Support ticket: <strong>' +
      String(deskTicketId).replace(/</g, "&lt;") +
      "</strong></p>"
    );
  }

  function clearIntakeBanners(form) {
    var loading = form.querySelector("[data-support-intake-loading]");
    if (loading) loading.remove();
    var err = form.querySelector("[data-support-intake-error]");
    if (err) err.remove();
  }

  function showIntakeLoading(form, message) {
    clearIntakeBanners(form);
    var btn = findSubmitButton(form);
    if (btn) {
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");
      if (!btn.getAttribute("data-support-intake-submit-label")) {
        btn.setAttribute("data-support-intake-submit-label", (btn.textContent || "").trim());
      }
      btn.textContent = message || DEFAULT_LOADING_MESSAGE;
    }
    var el = document.createElement("div");
    el.className = "pma-status-banner pma-status-banner--loading";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.setAttribute("data-support-intake-loading", "true");
    el.textContent = message || DEFAULT_LOADING_MESSAGE;
    form.insertBefore(el, form.firstChild);
  }

  function resetSubmitButton(form) {
    var btn = findSubmitButton(form);
    if (!btn) return;
    btn.disabled = false;
    btn.removeAttribute("aria-busy");
    var original = btn.getAttribute("data-support-intake-submit-label");
    if (original) btn.textContent = original;
  }

  function showIntakeSuccess(section, requestReference, deskStatus, deskTicketId, config) {
    var form = findSupportForm(section);
    if (!form) return;
    var template = section.querySelector("[data-support-intake-success-template]");
    var statusBody = deskStatusMessage(config, deskStatus || "dry_run");
    var ticketLine = deskTicketLine(deskStatus, deskTicketId);
    var html = "";
    if (template) {
      html = template.innerHTML
        .replace(/__REQUEST_REFERENCE__/g, requestReference || "")
        .replace(/__DESK_STATUS_BODY__/g, statusBody)
        .replace(/__DESK_TICKET_LINE__/g, ticketLine);
    } else {
      html =
        '<div class="pma-status-banner pma-status-banner--success" role="status" tabindex="-1">' +
        '<h3 class="pma-h4">Message received</h3>' +
        "<p>" + statusBody + "</p>" +
        ticketLine +
        (requestReference
          ? '<p class="pma-text-sm">Reference: <strong>' +
            String(requestReference).replace(/</g, "&lt;") +
            "</strong></p>"
          : "") +
        "</div>";
    }
    form.innerHTML = html;
    var banner = section.querySelector(".pma-status-banner--success, [id*='SupportFormSuccess']");
    if (banner) {
      if (!banner.getAttribute("role")) banner.setAttribute("role", "status");
      if (!banner.getAttribute("aria-live")) banner.setAttribute("aria-live", "polite");
      if (!banner.hasAttribute("tabindex")) banner.setAttribute("tabindex", "-1");
      try {
        banner.focus({ preventScroll: true });
      } catch (_focusErr) {
        try {
          banner.focus();
        } catch (_focusErr2) {}
      }
      if (typeof banner.scrollIntoView === "function") {
        banner.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }
  }

  function focusFirstInvalidField(form) {
    if (!form || typeof form.querySelector !== "function") return;
    var invalid = form.querySelector(":invalid");
    if (invalid && typeof invalid.focus === "function") {
      invalid.focus();
      if (typeof invalid.scrollIntoView === "function") {
        invalid.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }
  }


  function isTurnstileOrCfFailure(result) {
    if (!result) return false;
    var status = result.status;
    var body = result.body || {};
    var message = String(body.message || body.error || "");
    if (/cloudflare verification failed|turnstile|cf[-_ ]?turnstile|captcha/i.test(message)) {
      return true;
    }
    if (status === 403 && /cloudflare|turnstile|verification/i.test(message)) {
      return true;
    }
    return false;
  }

  function fallThroughToNativeSubmit(form) {
    if (!form) return false;
    form.removeAttribute("data-support-intake-submitting");
    resetSubmitButton(form);
    clearIntakeBanners(form);
    // Copy Turnstile token into the Shopify contact field when present.
    var token = form.getAttribute("data-turnstile-token") || "";
    var tokenInput = form.querySelector('[name="cf-turnstile-response"]');
    if (tokenInput && token) tokenInput.value = token;
    // Hash fragments on action break Shopify contact POST (400/500).
    try {
      var action = form.getAttribute("action") || "";
      if (action.indexOf("#") >= 0) {
        form.setAttribute("action", action.split("#")[0] || "/contact");
      }
    } catch (_actionErr) {}
    // Prevent infinite re-entry into the intake interceptor.
    form.setAttribute("data-support-intake-native-fallback", "true");
    try {
      if (typeof form.requestSubmit === "function") {
        form.requestSubmit();
      } else {
        form.submit();
      }
      return true;
    } catch (_e) {
      form.removeAttribute("data-support-intake-native-fallback");
      return false;
    }
  }
  function showIntakeError(form, message) {
    var loading = form.querySelector("[data-support-intake-loading]");
    if (loading) loading.remove();
    resetSubmitButton(form);
    resetTurnstile(form);
    var existing = form.querySelector("[data-support-intake-error]");
    if (existing) existing.remove();
    var el = document.createElement("div");
    el.className = "pma-status-banner pma-status-banner--error";
    el.setAttribute("role", "alert");
    el.setAttribute("data-support-intake-error", "true");
    el.textContent =
      message ||
      "We could not send your support message. Please try again, or contact support directly if the problem continues.";
    form.insertBefore(el, form.firstChild);
    el.focus();
    if (typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }

  function track(eventName, payload) {
    try {
      if (window.PMA && window.PMA.analytics && typeof window.PMA.analytics.track === "function") {
        window.PMA.analytics.track(eventName, payload || {});
      }
    } catch (_e) {}
  }

  function bindSection(section, turnstileSiteKey) {
    var config = readConfig(section);
    if (!config || !config.enabled) return;

    var form = findSupportForm(section);
    if (!form) return;

    if (!form.getAttribute("data-support-intake-rendered-at")) {
      form.setAttribute("data-support-intake-rendered-at", String(Date.now()));
    }
    try {
      var action = form.getAttribute("action") || "";
      if (action.indexOf("#") >= 0) {
        form.setAttribute("action", action.split("#")[0] || "/contact");
      }
    } catch (_a) {}
    ensureTurnstile(form, turnstileSiteKey || "");

    form.addEventListener("submit", function (event) {
      if (form.getAttribute("data-support-intake-native-fallback") === "true") {
        form.removeAttribute("data-support-intake-native-fallback");
        return;
      }
      if (form.getAttribute("data-support-intake-submitting") === "true") {
        event.preventDefault();
        return;
      }

      var honeypot = form.querySelector('[name="contact[website]"]');
      if (honeypot && honeypot.value) {
        event.preventDefault();
        return;
      }

      var cooldownMs = cooldownRemainingMs(form);
      if (cooldownMs > 0) {
        event.preventDefault();
        showIntakeError(
          form,
          config.msgCooldown.replace(
            "a few seconds",
            String(Math.ceil(cooldownMs / 1000)) + " seconds",
          ),
        );
        return;
      }

      if (!form.checkValidity()) {
        event.preventDefault();
        focusFirstInvalidField(form);
        if (typeof form.reportValidity === "function") {
          form.reportValidity();
        }
        return;
      }

      if (turnstileSiteKey && !form.getAttribute("data-turnstile-token")) {
        event.preventDefault();
        showIntakeError(form, config.msgTurnstileRequired);
        return;
      }

      event.preventDefault();
      form.setAttribute("data-support-intake-submitting", "true");
      markSubmitted(form);
      showIntakeLoading(form, config.msgSending);

      var payload = buildPayload(form, section);
      track("pma_support_submit_attempt", {
        category: (payload.category || "").slice(0, 64),
        origin: config.surface,
        intake_path: "bridge",
      });

      fetch(config.url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
        credentials: "omit",
      })
        .then(function (response) {
          return response
            .json()
            .catch(function () {
              return {};
            })
            .then(function (body) {
              return { ok: response.ok, status: response.status, body: body };
            });
        })
        .then(function (result) {
          form.removeAttribute("data-support-intake-submitting");
          if (result.ok && result.body && result.body.ok) {
            track("pma_support_submit_success", {
              origin: config.surface,
              intake_path: "bridge",
              duplicate: Boolean(result.body.duplicate),
              desk_projection_status: result.body.desk_projection_status || "unknown",
            });
            showIntakeSuccess(
              section,
              result.body.request_reference || "",
              result.body.desk_projection_status,
              result.body.desk_ticket_id || "",
              config,
            );
            return;
          }
          track("pma_support_submit_error", {
            error_type: "bridge_intake",
            status: result.status,
            origin: config.surface,
          });
          if (isTurnstileOrCfFailure(result) && fallThroughToNativeSubmit(form)) {
            track("pma_support_submit_native_fallback", {
              origin: config.surface,
              reason: "turnstile_or_cf_failure",
              status: result.status,
            });
            return;
          }
          showIntakeError(
            form,
            (result.body && result.body.message) ||
              (result.status === 429
                ? "Too many support requests from your network. Please wait about an hour and try again."
                : "We could not record your support request. Please try again or email support directly."),
          );
        })
        .catch(function () {
          form.removeAttribute("data-support-intake-submitting");
          track("pma_support_submit_error", { error_type: "network", origin: config.surface });
          showIntakeError(
            form,
            "Network error while submitting your request. Please try again or use email support.",
          );
        });
    });
  }

  function init() {
    fetchTurnstileConfig().then(function (turnstileConfig) {
      var siteKey =
        turnstileConfig && turnstileConfig.turnstileSiteKey
          ? turnstileConfig.turnstileSiteKey
          : "";
      for (var h = 0; h < SECTION_HANDLES.length; h += 1) {
        var handle = SECTION_HANDLES[h];
        var sections = document.querySelectorAll(
          '[data-section-handle="' + handle + '"][data-support-intake-enabled="true"]',
        );
        for (var i = 0; i < sections.length; i += 1) {
          bindSection(sections[i], siteKey);
        }
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
